import { useEffect, useRef, useState } from "react";
import PageHeader from "@/components/PageHeader";
import MapView from "@/components/MapView";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Camera } from "lucide-react";

export default function Fleet() {
  const [depot, setDepot] = useState(null);
  const [sites, setSites] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const seenIds = useRef(null); // null until first-load baseline is set
  const clientMap = useRef({});

  const tick = async () => {
    const [d, s, dv, cl, co] = await Promise.all([
      api.get("/settings/depot"),
      api.get("/sites"),
      api.get("/driver/locations"),
      api.get("/clients"),
      api.get("/collections"),
    ]);
    setDepot(d.data);
    setSites(s.data);
    setDrivers(dv.data);
    // build client map for toast enrichment
    cl.data.forEach((c) => (clientMap.current[c.id] = c.name));

    // Detect newly-created collections since last tick and pop a live toast
    const now = co.data;
    if (seenIds.current === null) {
      // first-load baseline — do not toast pre-existing rows
      seenIds.current = new Set(now.map((r) => r.id));
    } else {
      for (const row of now) {
        if (!seenIds.current.has(row.id)) {
          seenIds.current.add(row.id);
          const clientName = clientMap.current[row.client_id] || "a client site";
          toast.success(
            `Stop completed at ${clientName}`,
            {
              description: `${row.driver || "Driver"} · ${row.waste_type} · ${row.disposal_method}`,
              icon: "📸",
              duration: 6000,
            }
          );
        }
      }
    }
  };

  useEffect(() => {
    tick();
    const t = setInterval(tick, 30_000);
    return () => clearInterval(t);
  }, []);

  return (
    <div>
      <PageHeader
        eyebrow="Operations · Fleet"
        title="Live map"
        subtitle="All client sites, the HBT depot, and any driver currently on the road (updated every 30 seconds). Stop completions from the driver app appear here as live toasts."
      />
      <div className="px-8 lg:px-12 py-8 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="hbt-card p-5"><div className="hbt-label">Sites on map</div><div className="font-display text-3xl font-semibold mt-2">{sites.filter(s=>s.latitude && s.longitude).length}</div></div>
          <div className="hbt-card p-5"><div className="hbt-label">Drivers online</div><div className="font-display text-3xl font-semibold mt-2" style={{ color: "var(--forest)" }}>{drivers.length}</div><div className="text-xs text-neutral-500 mt-1">last 15 min</div></div>
          <div className="hbt-card p-5"><div className="hbt-label">Stops tracked</div><div className="font-display text-3xl font-semibold mt-2 font-mono-num">{seenIds.current ? seenIds.current.size : "—"}</div><div className="text-xs text-neutral-500 mt-1 inline-flex items-center gap-1"><Camera className="w-3 h-3" /> incl. photo-verified</div></div>
          <div className="hbt-card p-5"><div className="hbt-label">Depot</div><div className="font-display text-base font-semibold mt-2 truncate">{depot?.name || "—"}</div><div className="text-xs text-neutral-500 mt-1 font-mono-num">{depot ? `${depot.latitude.toFixed(4)}, ${depot.longitude.toFixed(4)}` : ""}</div></div>
        </div>
        <MapView depot={depot} sites={sites} drivers={drivers} height={600} />
      </div>
    </div>
  );
}
